'use client';

import React, { useState, useMemo } from 'react';
import { toast } from 'sonner';
import FlockTableFilters from './FlockTableFilters';
import FlockDataTable from './FlockDataTable';
import FlockDetailModal from './FlockDetailModal';
import CreateFlockModal from './CreateFlockModal';
import { MOCK_FLOCKS, FlockRecord } from './flockData';
import { useFlockPopulation } from '@/hooks/useFlockPopulation';
import { Layers, Plus, Download, Printer, BookOpen } from 'lucide-react';

export default function FlockManagementContent() {
  const [flocks, setFlocks] = useState<FlockRecord[]>(MOCK_FLOCKS);
  const [search, setSearch] = useState('');
  const [filterFarm, setFilterFarm] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterHouse, setFilterHouse] = useState('all');
  const [selectedFlock, setSelectedFlock] = useState<FlockRecord | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [sortCol, setSortCol] = useState<keyof FlockRecord>('arrivalDate');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  // ── Live flock population from TanStack Query (derived from movement ledger) ─
  const { data: flockPopulation = [] } = useFlockPopulation();

  // Merge ledger-derived population into flock records
  const enrichedFlocks = useMemo(() => {
    return flocks.map((f) => {
      const ledger = flockPopulation.find((fp) => fp.flockId === f.id);
      if (!ledger) return f;
      const currentQty = Math.max(0, ledger.currentBirds);
      const mortalityPct = f.initialQty > 0
        ? parseFloat(Math.max(0, ((f.initialQty - currentQty) / f.initialQty) * 100).toFixed(2))
        : f.mortalityPct;
      const survivalRate = parseFloat((100 - mortalityPct).toFixed(2));
      return { ...f, currentQty, mortalityPct, survivalRate };
    });
  }, [flocks, flockPopulation]);

  const ledgerLinkedCount = flockPopulation.length;

  const filtered = useMemo(() => {
    return enrichedFlocks
      .filter((f) => {
        const q = search.toLowerCase();
        const matchSearch = !q ||
          f.flockNumber.toLowerCase().includes(q) ||
          f.flockName.toLowerCase().includes(q) ||
          f.breed.toLowerCase().includes(q) ||
          f.house.toLowerCase().includes(q) ||
          f.farm.toLowerCase().includes(q);
        const matchFarm = filterFarm === 'all' || f.farm === filterFarm;
        const matchType = filterType === 'all' || f.birdType === filterType;
        const matchStatus = filterStatus === 'all' || f.status === filterStatus;
        const matchHouse = filterHouse === 'all' || f.house === filterHouse;
        return matchSearch && matchFarm && matchType && matchStatus && matchHouse;
      })
      .sort((a, b) => {
        const av = a[sortCol] as string | number;
        const bv = b[sortCol] as string | number;
        const cmp = typeof av === 'number' && typeof bv === 'number'
          ? av - bv
          : String(av).localeCompare(String(bv));
        return sortDir === 'asc' ? cmp : -cmp;
      });
  }, [enrichedFlocks, search, filterFarm, filterType, filterStatus, filterHouse, sortCol, sortDir]);

  function handleSort(col: keyof FlockRecord) {
    if (sortCol === col) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortCol(col); setSortDir('asc'); }
  }

  function handleViewFlock(flock: FlockRecord) {
    setSelectedFlock(flock);
    setDetailOpen(true);
  }

  function handleBulkArchive() {
    // Backend: PATCH /api/flocks/bulk-archive
    setFlocks((prev) => prev.map((f) => selectedIds.includes(f.id) ? { ...f, status: 'closed' } : f));
    toast.success(`${selectedIds.length} flock(s) archived`);
    setSelectedIds([]);
  }

  function handleCreateFlock(data: Partial<FlockRecord>) {
    // Backend: POST /api/flocks
    const newFlock: FlockRecord = {
      id: `flock-${Date.now()}`,
      flockNumber: `BR-2026-${String(flocks.length + 1).padStart(3, '0')}`,
      flockName: data.flockName ?? 'New Flock',
      birdType: data.birdType ?? 'Broiler',
      breed: data.breed ?? 'Cobb 500',
      purpose: data.purpose ?? 'Meat',
      farm: data.farm ?? 'Idudi Poultry Farm',
      house: data.house ?? 'House A1',
      supplier: data.supplier ?? 'Ugachick Hatchery',
      arrivalDate: data.arrivalDate ?? '04/09/2026',
      initialQty: data.initialQty ?? 2000,
      currentQty: data.initialQty ?? 2000,
      ageDays: 0,
      mortalityPct: 0,
      fcr: 0,
      avgWeightKg: 0,
      costPerChick: data.costPerChick ?? 2800,
      totalAcquisitionCost: (data.initialQty ?? 2000) * (data.costPerChick ?? 2800),
      expectedCycleDays: data.expectedCycleDays ?? 42,
      targetMarketDate: data.targetMarketDate ?? '16/10/2026',
      status: 'planned',
      survivalRate: 100,
    };
    setFlocks((prev) => [newFlock, ...prev]);
    toast.success(`Flock ${newFlock.flockNumber} created successfully`);
    setCreateOpen(false);
  }

  const activeCount = enrichedFlocks.filter((f) => f.status === 'active').length;
  const totalBirds = enrichedFlocks.reduce((s, f) => s + f.currentQty, 0);

  return (
    <div className="px-4 md:px-6 xl:px-8 py-5 max-w-screen-2xl mx-auto">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Layers size={20} style={{ color: 'var(--primary)' }} />
            <h1 className="text-2xl font-bold text-foreground">Flock Management</h1>
          </div>
          <p className="text-sm text-muted-foreground">
            {activeCount} active flocks · {totalBirds.toLocaleString()} total live birds
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button className="btn-secondary text-sm py-2 px-3">
            <Printer size={14} />
            <span className="hidden sm:inline">Print</span>
          </button>
          <button className="btn-secondary text-sm py-2 px-3">
            <Download size={14} />
            <span className="hidden sm:inline">Export</span>
          </button>
          <button
            onClick={() => setCreateOpen(true)}
            className="btn-primary text-sm"
          >
            <Plus size={15} />
            New Flock
          </button>
        </div>
      </div>

      {/* Ledger source banner */}
      {ledgerLinkedCount > 0 && (
        <div
          className="flex items-center gap-2 px-3 py-2 rounded-lg border mb-4 text-xs"
          style={{ backgroundColor: '#eff6ff', borderColor: '#bfdbfe', color: '#1d4ed8' }}
        >
          <BookOpen size={13} className="flex-shrink-0" />
          <span>
            <strong>Population figures sourced from Bird Movement Ledger</strong> — {ledgerLinkedCount} of {flocks.length} flocks linked. Only <em>Posted</em> movements are counted.
          </span>
        </div>
      )}

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        {[
          { id: 'fs-active', label: 'Active Flocks', value: flocks.filter(f => f.status === 'active').length, color: 'var(--success)', bg: 'var(--success-bg)' },
          { id: 'fs-planned', label: 'Planned', value: flocks.filter(f => f.status === 'planned').length, color: 'var(--info)', bg: 'var(--info-bg)' },
          { id: 'fs-completed', label: 'Completed / Sold', value: flocks.filter(f => ['completed','sold','closed'].includes(f.status)).length, color: 'var(--muted-foreground)', bg: 'var(--muted)' },
          { id: 'fs-birds', label: 'Total Live Birds', value: totalBirds.toLocaleString(), color: 'var(--primary)', bg: '#f0fdf4' },
        ].map((s) => (
          <div key={s.id} className="kpi-card flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: s.bg }}>
              <Layers size={16} style={{ color: s.color }} />
            </div>
            <div>
              <p className="text-xs text-muted-foreground font-medium">{s.label}</p>
              <p className="text-lg font-bold font-tabular text-foreground">{s.value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <FlockTableFilters
        search={search}
        onSearch={setSearch}
        filterFarm={filterFarm}
        onFarm={setFilterFarm}
        filterType={filterType}
        onType={setFilterType}
        filterStatus={filterStatus}
        onStatus={setFilterStatus}
        filterHouse={filterHouse}
        onHouse={setFilterHouse}
      />

      {/* Bulk action bar */}
      {selectedIds.length > 0 && (
        <div
          className="flex items-center justify-between px-4 py-2.5 rounded-lg border mb-3 slide-up"
          style={{ backgroundColor: '#eff6ff', borderColor: '#bfdbfe' }}
        >
          <span className="text-sm font-medium" style={{ color: 'var(--info)' }}>
            {selectedIds.length} flock(s) selected
          </span>
          <div className="flex items-center gap-2">
            <button onClick={() => setSelectedIds([])} className="btn-ghost text-xs py-1 px-2">
              Clear
            </button>
            <button onClick={handleBulkArchive} className="text-xs font-medium px-3 py-1.5 rounded" style={{ backgroundColor: 'var(--danger)', color: '#fff' }}>
              Archive Selected
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <FlockDataTable
        flocks={filtered}
        selectedIds={selectedIds}
        onSelectIds={setSelectedIds}
        onView={handleViewFlock}
        sortCol={sortCol}
        sortDir={sortDir}
        onSort={handleSort}
        allFlocks={flocks}
        onFlockUpdate={(updated) => setFlocks((prev) => prev.map((f) => f.id === updated.id ? updated : f))}
        onFlockDelete={(id) => {
          setFlocks((prev) => prev.filter((f) => f.id !== id));
          toast.success('Flock removed');
        }}
      />

      {/* Modals */}
      {detailOpen && selectedFlock && (
        <FlockDetailModal
          flock={selectedFlock}
          open={detailOpen}
          onClose={() => { setDetailOpen(false); setSelectedFlock(null); }}
        />
      )}
      {createOpen && (
        <CreateFlockModal
          open={createOpen}
          onClose={() => setCreateOpen(false)}
          onCreate={handleCreateFlock}
        />
      )}
    </div>
  );
}